'use client';

import { useTranslation } from 'react-i18next';

import type { ProjectPayload } from '@/app/models/providersResponse';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import DataTable, { type Column, bodyCellSx } from '@/components/common/DataTable';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, TableCell } from '@mui/material';

interface ProjectTableProps {
  projects: ProjectPayload[];
  onRemove?: (projectId: number) => void;
}

export default function ProjectTable({ projects, onRemove }: ProjectTableProps) {
  const { t } = useTranslation(['provider', 'common']);

  const handleDelete = (id: number) => {
    if (onRemove) {
      onRemove(id);
    }
  };

  const columns: Column[] = [
    {
      key: 'project',
      label: t('project'),
      align: 'left',
      width: '50%',
    },
    {
      key: 'value',
      label: t('value.r$'),
      align: 'left',
      width: '30%',
    },
    {
      key: 'actions',
      label: t('actions'),
      align: 'center',
      width: '20%',
    },
  ];

  const renderRow = (project: ProjectPayload) => (
    <>
      <TableCell sx={bodyCellSx}>{`${project.projectCode} - ${project.projectName}`}</TableCell>
      <TableCell sx={bodyCellSx}>{formatterMonetaryValue(project.extraValue)}</TableCell>
      <TableCell sx={bodyCellSx} align="center">
        <IconButton size="small" onClick={() => handleDelete(project.projectId)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </>
  );

  const getRowKey = (project: ProjectPayload) => project.projectId;

  return (
    <DataTable
      columns={columns}
      data={projects}
      renderRow={renderRow}
      emptyMessage={t('no.projects.added')}
      getRowKey={getRowKey}
      size="small"
      maxHeight="20rem"
      stickyHeader={false}
    />
  );
}
